import api from './api';
import { ApiResponse, unwrapApiResponse } from '../utils/api-response.util';

export interface Feature {
  id: number;
  key: string;
  name: string;
  description: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateFeatureDto {
  key: string;
  name: string;
  description?: string;
}

export interface UpdateFeatureDto {
  name?: string;
  description?: string;
}

/** Expert feature registry + expert-plan grants. Isolated from client-entitlements.service.ts — own endpoints, own data, mirrors api/src/modules/expert-entitlements. */
class ExpertEntitlementsService {
  private readonly BASE_URL = '/admin/entitlements/experts';

  async listFeatures(): Promise<Feature[]> {
    const response = await api.get<ApiResponse<Feature[]>>(`${this.BASE_URL}/features`);
    return unwrapApiResponse<Feature[]>(response);
  }

  async createFeature(dto: CreateFeatureDto): Promise<Feature> {
    const response = await api.post<ApiResponse<Feature>>(`${this.BASE_URL}/features`, dto);
    return unwrapApiResponse<Feature>(response);
  }

  async updateFeature(id: number, dto: UpdateFeatureDto): Promise<Feature> {
    const response = await api.patch<ApiResponse<Feature>>(`${this.BASE_URL}/features/${id}`, dto);
    return unwrapApiResponse<Feature>(response);
  }

  async deleteFeature(id: number): Promise<void> {
    await api.delete<ApiResponse<{ id: number }>>(`${this.BASE_URL}/features/${id}`);
  }

  async getPlanFeatures(planId: number): Promise<string[]> {
    const response = await api.get<ApiResponse<string[]>>(`${this.BASE_URL}/plans/${planId}/features`);
    return unwrapApiResponse<string[]>(response);
  }

  async setPlanFeatures(planId: number, featureKeys: string[]): Promise<string[]> {
    const response = await api.patch<ApiResponse<string[]>>(`${this.BASE_URL}/plans/${planId}/features`, { featureKeys });
    return unwrapApiResponse<string[]>(response);
  }
}

export const expertEntitlementsService = new ExpertEntitlementsService();
